/**
 * Buy Options — drawer open/close, variant + selling-plan selection, AJAX
 * add-to-cart.
 *
 * No build step needed, plain browser JS. Loaded with `defer` from the
 * section file, so it runs after the DOM is parsed.
 *
 * Pricing is never computed here. Every variant × selling-plan combination is
 * rendered server-side into the JSON block that
 * `snippets/buy-option-variant-data.liquid` puts inside each drawer, so all
 * money formatting stays in Liquid; this file only looks the current pair up
 * and writes the strings into the DOM.
 *
 * Cart integration:
 *   1. POST a JSON body to `${Shopify.routes.root}cart/add.js`, asking for the
 *      `cart-drawer` section back in the same response (Section Rendering API).
 *   2. Splice that freshly rendered markup into the live `cart-drawer` and open
 *      it. One request, so the drawer opens as soon as the item is in the cart.
 *   3. Refresh the rest of the cart UI (most importantly the header's
 *      `#cart-icon-bubble`, which lives in the layout and therefore can't come
 *      back from the Section Rendering API) through the theme's shared
 *      `window.refreshCartDrawer()` — in the background, because it re-fetches
 *      the whole current page and awaiting that is what used to make add-to-cart
 *      feel slow.
 *
 * Reliability: cart writes are serialized (Shopify rejects concurrent cart
 * mutations), transient failures are retried once, and a failure is reported
 * inline in the form instead of through a blocking `alert()`.
 *
 * Scroll-jump fix: opening the theme's cart-drawer toggles an
 * `overflow-hidden` class on <html> and moves focus into the drawer for
 * accessibility. In this theme that combination visibly snaps the page
 * back to the top (a pre-existing quirk — it also happens on other pages
 * whenever the cart drawer opens, not something specific to this module).
 * Since this can't be fixed from a single section's files, we work around
 * it here: capture the scroll position right before opening any drawer
 * (ours or the theme's cart drawer) and restore it right after — both on
 * the next animation frame and again after the drawer's own open
 * transition/focus-trap has had time to run.
 */
(function () {
  'use strict';

  var CART_SECTION = 'cart-drawer';
  var MAX_RETRIES = 1;
  var RETRY_DELAY = 350;
  var GENERIC_ERROR = 'Das hat leider nicht funktioniert. Bitte versuch es noch einmal.';

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function preserveScrollAcross(action) {
    var scrollY = window.scrollY || window.pageYOffset || 0;
    action();
    var restore = function () {
      window.scrollTo(0, scrollY);
    };
    requestAnimationFrame(restore);
    // The theme's cart-drawer applies its open styles/focus-trap on
    // transitionend, which can land a bit later than the next frame.
    setTimeout(restore, 350);
  }

  function initDrawers(root) {
    root.querySelectorAll('[data-lf-open-drawer]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-lf-open-drawer');
        var dialog = document.getElementById(id);
        if (dialog && typeof dialog.showModal === 'function') {
          preserveScrollAcross(function () {
            dialog.showModal();
          });
        }
      });
    });

    root.querySelectorAll('dialog.lf-drawer').forEach(function (dialog) {
      // Close button inside the drawer
      dialog.querySelectorAll('[data-lf-close-drawer]').forEach(function (closeBtn) {
        closeBtn.addEventListener('click', function () {
          dialog.close();
        });
      });

      // Click on the backdrop (outside the drawer content) closes it
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) {
          dialog.close();
        }
      });
    });
  }

  /* ---------------------------------------------------------------------- *
   * Variant + selling plan selection
   * ---------------------------------------------------------------------- */

  function getVariantData(dialog) {
    if (dialog.__lfVariantData !== undefined) return dialog.__lfVariantData;

    var node = dialog.querySelector('script[data-lf-variant-data]');
    var parsed = null;
    if (node) {
      try {
        parsed = JSON.parse(node.textContent);
      } catch (error) {
        console.error('[buy-options] variant price data is not valid JSON:', error);
      }
    }
    dialog.__lfVariantData = parsed;
    return parsed;
  }

  function currentVariantId(dialog) {
    var checked = dialog.querySelector('input[data-lf-variant-radio]:checked');
    if (checked) return checked.value;
    var hidden = dialog.querySelector('[data-lf-variant-input]');
    return hidden ? hidden.value : null;
  }

  // Keeps the plan radios, the price row, the unit price and — most
  // importantly — the hidden `id` field of the add-to-cart form in sync with
  // whatever variant and plan are currently selected.
  function syncDrawer(dialog) {
    var variantId = currentVariantId(dialog);
    var hidden = dialog.querySelector('[data-lf-variant-input]');
    if (hidden && variantId) hidden.value = variantId;

    var data = getVariantData(dialog);
    var variant = data && variantId ? data[variantId] : null;
    if (!variant) return;

    // A selling plan is only offered for the variants it has an allocation
    // for. Anything else would be rejected by cart/add.js.
    var plans = variant.plans || {};
    var planRadios = toArray(dialog.querySelectorAll('input[data-lf-plan-radio]'));
    planRadios.forEach(function (radio) {
      var offered = Object.prototype.hasOwnProperty.call(plans, radio.value);
      radio.disabled = !offered;
      if (!offered) radio.checked = false;
      var label = radio.closest('.lf-radio');
      if (label) label.classList.toggle('lf-radio--unavailable', !offered);
    });

    var checkedPlan = planRadios.filter(function (radio) {
      return radio.checked;
    })[0];
    if (!checkedPlan) {
      // "Einzelkauf" (no plan) wins over auto-picking a plan; when the
      // shopper's plan just became unavailable for the new variant, fall back
      // to it as well. Subscription-only sheets have no such radio and keep
      // pre-selecting the first plan.
      var oneTime = dialog.querySelector('input[data-lf-one-time-radio]');
      if (oneTime) {
        oneTime.checked = true;
      } else {
        checkedPlan = planRadios.filter(function (radio) {
          return !radio.disabled;
        })[0];
        if (checkedPlan) checkedPlan.checked = true;
      }
    }

    var pricing = (checkedPlan && plans[checkedPlan.value]) || variant;

    // Every pill's price and "Du sparst X%" ribbon follow the selected plan —
    // a subscription plan discounts each variant differently.
    var planId = checkedPlan ? checkedPlan.value : null;
    toArray(dialog.querySelectorAll('input[data-lf-variant-radio]')).forEach(function (radio) {
      var pillVariant = data[radio.value];
      if (!pillVariant) return;
      var pillPricing = (planId && pillVariant.plans && pillVariant.plans[planId]) || pillVariant;
      var pill = radio.closest('.lf-variant');
      if (!pill) return;

      var pillPrice = pill.querySelector('[data-lf-variant-price]');
      if (pillPrice) pillPrice.textContent = pillPricing.price;

      var pillSavings = pill.querySelector('[data-lf-variant-savings]');
      if (pillSavings) {
        pillSavings.textContent = pillPricing.savings || '';
        pillSavings.hidden = !pillPricing.savings;
      }
    });

    var priceEl = dialog.querySelector('[data-lf-price-el]');
    if (priceEl) priceEl.textContent = pricing.price;

    var compareEl = dialog.querySelector('[data-lf-compare-el]');
    if (compareEl) {
      var showCompare = Boolean(pricing.hasDiscount && pricing.compareAt);
      compareEl.textContent = pricing.compareAt || '';
      compareEl.hidden = !showCompare;
    }

    var badgeEl = dialog.querySelector('[data-lf-badge-el]');
    if (badgeEl) {
      badgeEl.textContent = pricing.badge || '';
      badgeEl.hidden = !pricing.badge;
      badgeEl.classList.remove('lf-pill--dark', 'lf-pill--light', 'lf-pill--neutral');
      badgeEl.classList.add(pricing.badgeClass || 'lf-pill--neutral');
    }

    var unitEl = dialog.querySelector('[data-lf-unit-el]');
    if (unitEl) {
      unitEl.textContent = variant.unitPrice ? '· ' + variant.unitPrice : '';
      unitEl.hidden = !variant.unitPrice;
    }

    var submit = dialog.querySelector('button[type="submit"]');
    if (submit) submit.disabled = variant.available === false;
  }

  function initVariantSync(root) {
    root.querySelectorAll('dialog.lf-drawer').forEach(function (dialog) {
      dialog
        .querySelectorAll('input[data-lf-variant-radio], input[data-lf-plan-radio], input[data-lf-one-time-radio]')
        .forEach(function (radio) {
          radio.addEventListener('change', function () {
            syncDrawer(dialog);
          });
        });
      syncDrawer(dialog);
    });
  }

  /* ---------------------------------------------------------------------- *
   * Add to cart
   * ---------------------------------------------------------------------- */

  function setLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
  }

  function showFormError(form, message) {
    var target = form.querySelector('[data-lf-error]');
    if (!target) {
      // No slot rendered (older markup) — fall back to not losing the message.
      alert(message);
      return;
    }
    target.textContent = message;
    target.hidden = false;
  }

  function clearFormError(form) {
    var target = form.querySelector('[data-lf-error]');
    if (target) {
      target.textContent = '';
      target.hidden = true;
    }
  }

  var CHECK_ICON =
    '<svg width="{size}" height="{size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="{style}">' +
    '<polyline points="20 6 9 17 4 12"></polyline></svg>';

  function flashSuccess(button) {
    if (!button) return;
    var original = button.innerHTML;
    var isCircle = button.classList.contains('lf-btn-circle');

    if (isCircle) {
      // No room for text in the 40px circle — just show a checkmark icon.
      button.innerHTML = CHECK_ICON.replace(/\{size\}/g, '16').replace('{style}', 'display:block');
    } else {
      button.innerHTML =
        CHECK_ICON.replace(/\{size\}/g, '18').replace(
          '{style}',
          'display:inline-block;vertical-align:-3px;margin-right:6px'
        ) + 'Hinzugefügt';
    }

    setTimeout(function () {
      button.innerHTML = original;
    }, 1600);
  }

  function getCartIconBubble() {
    return (
      document.querySelector('#cart-icon-bubble[role="button"]') ||
      document.querySelector('#cart-icon-bubble')
    );
  }

  // Open the theme's own cart drawer directly via its custom element API
  // (same element the theme's native product forms target) instead of
  // synthetically clicking the header icon — one less moving part, and it
  // sidesteps any link/anchor default-navigation behavior entirely.
  function openThemeCartDrawer() {
    preserveScrollAcross(function () {
      var cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl && typeof cartDrawerEl.open === 'function') {
        cartDrawerEl.open();
      } else {
        var openCartButton = getCartIconBubble();
        if (openCartButton) openCartButton.click();
      }
    });
  }

  // Swap in the cart-drawer markup that came back with the add response.
  // Returns false when the response didn't carry it, so the caller can fall
  // back to the theme's own (slower) re-render.
  function applyCartSections(sections) {
    if (!sections || !sections[CART_SECTION]) return false;

    var parsed = new DOMParser().parseFromString(sections[CART_SECTION], 'text/html');
    var newDrawer = parsed.querySelector('cart-drawer');
    var oldDrawer = document.querySelector('cart-drawer');
    if (!newDrawer || !oldDrawer) return false;

    oldDrawer.innerHTML = newDrawer.innerHTML;
    // We just added a line item, so the cart cannot be empty.
    if (typeof window.syncCartEmptyState === 'function') window.syncCartEmptyState(1);
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('rerenderCart'));
    }, 100);
    return true;
  }

  // Shopify rejects overlapping cart writes, and this section can have several
  // add-to-cart buttons on screen at once — so every request waits for the
  // previous one instead of racing it.
  var cartQueue = Promise.resolve();

  function enqueueCartWrite(task) {
    var result = cartQueue.then(task, task);
    cartQueue = result.then(
      function () {},
      function () {}
    );
    return result;
  }

  function requestAdd(payload, attempt) {
    return fetch(routesRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(
      function (response) {
        return response.text().then(function (text) {
          var data = null;
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            data = null;
          }

          if (response.ok && data && !data.status) return data;

          // A parseable error body means Shopify said no on purpose (sold out,
          // invalid selling plan, …) — retrying that would fail identically.
          // Everything else (throttling, 5xx, a non-JSON edge/security page) is
          // worth one more attempt.
          var retriable = !data || response.status === 429 || response.status >= 500;
          if (retriable && attempt < MAX_RETRIES) {
            return wait(RETRY_DELAY * (attempt + 1)).then(function () {
              return requestAdd(payload, attempt + 1);
            });
          }

          var error = new Error((data && (data.description || data.message)) || GENERIC_ERROR);
          error.cartStatus = response.status;
          error.cartBody = data || text.slice(0, 300);
          throw error;
        });
      },
      function (networkError) {
        if (attempt < MAX_RETRIES) {
          return wait(RETRY_DELAY * (attempt + 1)).then(function () {
            return requestAdd(payload, attempt + 1);
          });
        }
        throw networkError;
      }
    );
  }

  async function handleAddToCart(form) {
    var button = form.querySelector('button[type="submit"]');
    if (button && button.disabled) return;

    clearFormError(form);
    setLoading(button, true);

    var formData = new FormData(form);
    var payload = {
      items: [
        {
          id: formData.get('id'),
          quantity: formData.get('quantity') || 1,
        },
      ],
      sections: CART_SECTION,
      sections_url: window.location.pathname + window.location.search,
    };
    var sellingPlan = formData.get('selling_plan');
    if (sellingPlan) {
      payload.items[0].selling_plan = sellingPlan;
    }

    try {
      var data = await enqueueCartWrite(function () {
        return requestAdd(payload, 0);
      });

      var rendered = applyCartSections(data && data.sections);
      if (!rendered && typeof window.refreshCartDrawer === 'function') {
        await window.refreshCartDrawer();
      }

      flashSuccess(button);
      document.dispatchEvent(new CustomEvent('lf:cart:updated', { detail: { item: data } }));
      openThemeCartDrawer();

      // The header cart bubble is part of the layout, not of the cart-drawer
      // section, so it can only be refreshed by re-reading the page. Do it
      // after the drawer is already open — the shopper never waits for it.
      if (rendered && typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer();
      }

      // If this add-to-cart happened inside an open drawer, close it.
      var parentDialog = form.closest('dialog.lf-drawer');
      if (parentDialog) {
        setTimeout(function () {
          parentDialog.close();
        }, 400);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[buy-options] add to cart failed:', err, err && err.cartBody);
      showFormError(form, (err && err.message) || GENERIC_ERROR);
    } finally {
      setLoading(button, false);
    }
  }

  function initForms(root) {
    root.querySelectorAll('form.lf-atc-form, form.lf-drawer-form').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        handleAddToCart(form);
      });
    });
  }

  function init(root) {
    if (root) {
      initDrawers(root);
      initForms(root);
      initVariantSync(root);
      return;
    }
    // A page can hold more than one Buy Options section.
    document.querySelectorAll('.lf-buy-options').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Re-init if the section is re-rendered live in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
